import { useRef, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { colors } from '../../../style';
import { UploadStatus } from '../../../utils/upload-activities';
import { HelpPopup } from '../../base/claim-card/components/tw-components';

const labels: Record<
  UploadStatus,
  { text: string; hint: string; className: string }
> = {
  processing: {
    text: 'Processing',
    hint: 'This could take a few minutes',
    className: 'tw-bg-unm-pending-bg tw-text-unm-adhoc2',
  },
};

type Props = {
  id: string;
  status?: UploadStatus;
};

/**
 * Status pill for an uploaded document that isn't available yet. Shows its
 * hint on hover, focus, or tap, in the same popup as other help text.
 */
export const UploadStatusLabel = ({ id, status }: Props) => {
  const [showHint, setShowHint] = useState(false);
  const labelRef = useRef<HTMLButtonElement | null>(null);

  if (!status) {
    return null;
  }
  const { text, hint, className } = labels[status];

  return (
    <>
      <button
        type="button"
        id={id}
        ref={labelRef}
        className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-border-0 tw-px-2 tw-py-1 tw-text-sm tw-font-semibold tw-cursor-default ${className}`}
        onMouseEnter={() => setShowHint(true)}
        onMouseLeave={() => setShowHint(false)}
        onFocus={() => setShowHint(true)}
        onBlur={() => setShowHint(false)}
        // a tap also fires hover and focus, so only ever open here; tapping
        // elsewhere blurs the label and closes the hint
        onClick={() => setShowHint(true)}
      >
        {status === 'processing' && (
          <span aria-hidden="true" className="tw-flex">
            <ClipLoader size={12} color={colors.blue} />
          </span>
        )}
        {text}
        <span className="tw-sr-only">: {hint}</span>
      </button>
      <HelpPopup show={showHint} anchor={labelRef.current}>
        {hint}
      </HelpPopup>
    </>
  );
};
